/**
 * @fileoverview A function that provides the assignments API.
 *
 * @example
 * const { result } = renderHook(() => useAssignmentsAPI())
 */

import { createApi } from '@reduxjs/toolkit/query/react'
import { axiosBaseQuery } from './utils/axiosBaseQuery'

/**
 * @function assignmentsApi
 * @description Provides the assignments API.
 * @returns {ReturnType<typeof createApi>} - The assignments API.
 */
export const assignmentsApi = createApi({
    reducerPath: 'assignmentsApi',
    baseQuery: axiosBaseQuery({ baseUrl: `/private/` }),
    tagTypes: ['Assignment', 'AssignmentCounter'],
    endpoints: (builder) => ({
        getAssignments: builder.query<Novoic.API.Assignment[], void>({
            query: () => {
                return {
                    url: 'assignments',
                    method: 'GET',
                }
            },
            providesTags: (result) => {
                return [
                    ...result.map(({ id }) => ({ type: 'Assignment' as const, id })),
                    { type: 'AssignmentCounter' as const, id: 'all' },
                ]
            },
        }),

        getAssignmentById: builder.query<Novoic.API.Assignment, string>({
            query: (assignmentId: string) => {
                return {
                    url: `assignments/${assignmentId}`,
                    method: 'GET',
                }
            },
            providesTags: (result, error, args) => {
                return [{ type: 'Assignment' as const, id: args }]
            },
        }),

        getAssignmentsByParticipantId: builder.query<Novoic.API.Assignment[], string>({
            query: (participantId: string) => {
                const sp = new URLSearchParams(location.search)
                sp.set('patient', participantId)
                return {
                    url: `assignments?${sp.toString()}`,
                    method: 'GET',
                }
            },
            providesTags: (result, error, args) => {
                return [
                    ...result.map(({ id }) => ({ type: 'Assignment' as const, id })),
                    { type: 'AssignmentCounter' as const, id: args },
                ]
            },
        }),

        getAssignmentsCountByParticipantId: builder.query<{ count: number }, string>({
            query: (participantId: string) => {
                const sp = new URLSearchParams(location.search)
                sp.set('patient', participantId)
                return {
                    url: `assignments/count?${sp.toString()}`,
                    method: 'GET',
                }
            },
            providesTags: (result, error, args) => {
                return [{ type: 'AssignmentCounter' as const, id: args }]
            },
        }),
    }),
})

export const {
    useGetAssignmentByIdQuery,
    useGetAssignmentsQuery,
    useGetAssignmentsByParticipantIdQuery,
    useGetAssignmentsCountByParticipantIdQuery,
} = assignmentsApi
